import { AerCheckboxGroup } from "@aerstudios/aerated-aercheckboxgroup";
import { AerInputStructure } from "@aerstudios/aerated-aerinputstructure";
import type { WidgetProps } from "@rjsf/utils";
import cx from "classnames";
import type { JSONSchema7 } from "json-schema";
import React, { type MouseEventHandler } from "react";
import CheckboxTickURL from "../../../images/svgs/misc/dt_checkbox_tick.svg";
import { useFormBuilderContext } from "../FormBuilder/FormBuilder.context";
import * as styles from "./CheckboxGroup.module.scss";

type Props = WidgetProps<Props>;

export const CheckboxGroup: React.FC<Props> = ({
	onFocus: _onFocus,
	onBlur: _onBlur,
	className: _className,
	readonly,
	...aerCheckboxGroupProps
}) => {
	const {
		uiSchema: _uiSchema,
		formContext: _formContext,
		rawErrors: _rawErrors,
		hideLabel: _hideLabel,
		hideError: _hideError,
		autofocus: autoFocus,

		...aerCheckboxGroupPropsRest
	} = aerCheckboxGroupProps;
	const { anyOf, allOf, oneOf } = aerCheckboxGroupProps.schema;
	const { enumOptions } = aerCheckboxGroupProps.options;

	const enums = enumOptions?.map((option) => ({ title: option.label }));
	const items = (anyOf || allOf || oneOf || enums) as JSONSchema7[];

	const { onChange, value, ...inputProps } = aerCheckboxGroupPropsRest;
	const { isLastStep } = useFormBuilderContext();

	const handleChange =
		(v: string): MouseEventHandler<HTMLInputElement> =>
		(e) => {
			const { currentTarget } = e;
			const prevSet = new Set(value);
			const verb = currentTarget.checked ? "add" : "delete";

			prevSet[verb](v);

			const nextState = [...prevSet];

			onChange(nextState);
		};

	return (
		<AerInputStructure.Root errorMessage={_rawErrors?.[0]}>
			<AerCheckboxGroup.Root
				{...inputProps}
				id={`root_${aerCheckboxGroupPropsRest?.name}`}
				tabIndex={0}
				groupLabel={{
					text: aerCheckboxGroupPropsRest.label,
					hide: true,
				}}
				className={isLastStep && styles.lastStepCheckboxGroup}
				value={value}
				autoFocus={autoFocus}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
					}
				}}
			>
				{items?.map((answer, i) => {
					return (
						<AerCheckboxGroup.Item
							// TODO: Possibly remove if we are using our own validator
							key={answer.title}
							label={answer.title}
							value={answer.title}
							checkBox={<span className={styles.checkbox} />}
							checkedIcon={
								<span className={styles.checkedIcon}>
									<img
										src={CheckboxTickURL}
										className={styles.checkboxTick}
										aria-hidden="true"
										alt="checkbox tick"
									/>
								</span>
							}
							readOnly={readonly}
							className={cx(styles.checkboxItem, {
								[styles.firstItem]: i === 0,
							})}
							onClick={handleChange(answer.title)}
						/>
					);
				})}
			</AerCheckboxGroup.Root>
			<AerInputStructure.ErrorMessage />
		</AerInputStructure.Root>
	);
};
export default CheckboxGroup;
